import { useMemo } from 'react';

/**
 * Barras empilhadas de vendas por vendedor × mês — porte do web novo do
 * prospect (Fase 27), restilizado pro slate dark do painel.
 */
interface Series {
  vendedorId: number;
  vendedorNome: string;
  countsByMonth: number[];
}

interface Props {
  months: string[];
  series: Series[];
  maxSeries?: number;
  barAreaHeight?: number;
}

// Paleta categórica: as cores precisam ser distinguíveis entre si, não
// semânticas — por isso não vem dos tokens.
const PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#a78bfa', '#ef4444', '#06b6d4', '#94a3b8',
];

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function ymToShort(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return ym;
  return MONTH_LABELS[Number(m[2]) - 1] ?? ym;
}

export default function StackedBarChart({
  months, series, maxSeries = 6, barAreaHeight = 200,
}: Props) {
  const { displayed, monthTotals, maxTotal } = useMemo(() => {
    const visible = series.slice(0, maxSeries);
    const overflow = series.slice(maxSeries);
    let displayed: Series[] = visible;
    if (overflow.length > 0) {
      const aggregated: Series = {
        vendedorId: -1,
        vendedorNome: `Outros (${overflow.length})`,
        countsByMonth: months.map((_, i) =>
          overflow.reduce((acc, s) => acc + (s.countsByMonth[i] ?? 0), 0)
        ),
      };
      displayed = [...visible, aggregated];
    }
    const monthTotals = months.map((_, i) =>
      displayed.reduce((acc, s) => acc + (s.countsByMonth[i] ?? 0), 0)
    );
    const maxTotal = Math.max(1, ...monthTotals);
    return { displayed, monthTotals, maxTotal };
  }, [series, months, maxSeries]);

  return (
    <div>
      {/* Barras */}
      <div className="flex items-end gap-2 px-1" style={{ height: barAreaHeight }}>
        {months.map((ym, i) => {
          const total = monthTotals[i] ?? 0;
          const colH = total === 0 ? 0 : (total / maxTotal) * (barAreaHeight - 24);
          return (
            <div key={ym} className="flex-1 flex flex-col items-center">
              <span className="text-[10px] font-bold text-fg-2 mb-0.5 h-3">
                {total > 0 ? total : ''}
              </span>
              <div
                className="w-full rounded-md overflow-hidden flex flex-col-reverse"
                style={{ height: colH }}
              >
                {displayed.map((s, idx) => {
                  const cnt = s.countsByMonth[i] ?? 0;
                  if (cnt === 0) return null;
                  const segH = total > 0 ? (cnt / total) * colH : 0;
                  return (
                    <div
                      key={s.vendedorId}
                      style={{ height: segH, backgroundColor: PALETTE[idx] ?? '#94a3b8' }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Labels do eixo X */}
      <div className="flex gap-2 px-1 mt-1">
        {months.map((ym) => (
          <div key={`lbl-${ym}`} className="flex-1 text-center">
            <span className="text-[11px] text-fg-2">{ymToShort(ym)}</span>
          </div>
        ))}
      </div>

      {/* Legenda */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 px-1">
        {displayed.map((s, idx) => {
          const total = s.countsByMonth.reduce((a, b) => a + b, 0);
          return (
            <div key={`leg-${s.vendedorId}`} className="flex items-center">
              <span
                className="w-3 h-3 rounded mr-1.5"
                style={{ backgroundColor: PALETTE[idx] ?? '#94a3b8' }}
              />
              <span className="text-xs text-fg-2">{s.vendedorNome}</span>
              <span className="text-xs text-muted ml-1">({total})</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
